(() => {
  const drawer = document.querySelector("[data-cart-drawer]");
  if (!drawer || drawer.dataset.cartDrawerReady === "true") return;
  drawer.dataset.cartDrawerReady = "true";

  const formatter = new Intl.NumberFormat(
    document.documentElement.lang || "fr-FR",
    { style: "currency", currency: drawer.dataset.currency || "EUR" },
  );

  const formatMoney = (cents) => formatter.format(Number(cents || 0) / 100);
  const panel = drawer.querySelector(".cart-drawer__panel");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  let closeTimer = null;
  let transitionEndHandler = null;

  const cancelScheduledClose = () => {
    window.clearTimeout(closeTimer);
    closeTimer = null;
    if (panel && transitionEndHandler) {
      panel.removeEventListener("transitionend", transitionEndHandler);
    }
    transitionEndHandler = null;
  };

  const setFeedback = (message = "", status = "success") => {
    const feedback = drawer.querySelector("[data-cart-drawer-feedback]");
    if (!feedback) return;
    feedback.textContent = message;
    feedback.dataset.status = status;
    feedback.hidden = !message;
  };

  const setBusy = (busy) => {
    drawer.setAttribute("aria-busy", busy ? "true" : "false");
    drawer.querySelectorAll("[data-cart-drawer-quantity], [data-cart-drawer-remove]").forEach((control) => {
      control.disabled = busy;
    });
  };

  const updateHeaderCount = (itemCount) => {
    const trigger = document.querySelector("[data-cart-drawer-trigger]");
    if (!trigger) return;
    let count = trigger.querySelector(".cart-count");
    if (itemCount <= 0) {
      count?.remove();
    } else {
      if (!count) {
        count = document.createElement("span");
        count.className = "cart-count";
        trigger.append(count);
      }
      count.textContent = itemCount;
    }
    trigger.setAttribute(
      "aria-label",
      itemCount === 1 ? "Panier, 1 article" : `Panier, ${itemCount} articles`,
    );
  };

  const optimizedImageUrl = (source) => {
    if (!source) return "";
    const url = new URL(source, window.location.origin);
    url.searchParams.set("width", "200");
    return url.href;
  };

  const createQuantityButton = (label, action, path) => {
    const button = document.createElement("button");
    button.type = "button";
    button.dataset.cartDrawerQuantity = action;
    button.setAttribute("aria-label", label);
    button.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${path}" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="2"/></svg>`;
    return button;
  };

  const visibleProperties = (properties) =>
    Object.entries(properties || {}).filter(
      ([name, value]) => value && !name.startsWith("_"),
    );

  const createCartItem = (item) => {
    const row = document.createElement("li");
    row.className = "cart-drawer__item";
    row.dataset.cartDrawerItem = item.key;

    const media = document.createElement("a");
    media.className = "cart-drawer__media";
    media.href = item.url;
    media.setAttribute("aria-label", item.product_title);
    if (item.image) {
      const image = document.createElement("img");
      image.src = optimizedImageUrl(item.image);
      image.alt = item.featured_image?.alt || item.product_title;
      image.width = 100;
      image.height = 100;
      media.append(image);
    } else {
      const placeholder = document.createElement("span");
      placeholder.className = "cart-drawer__placeholder";
      placeholder.textContent = item.product_title.slice(0, 1).toUpperCase();
      placeholder.setAttribute("aria-hidden", "true");
      media.append(placeholder);
    }

    const body = document.createElement("div");
    body.className = "cart-drawer__item-body";
    const title = document.createElement("a");
    title.className = "cart-drawer__item-title";
    title.href = item.url;
    title.textContent = item.product_title;
    body.append(title);

    if (item.variant_title && item.variant_title !== "Default Title") {
      const variant = document.createElement("p");
      variant.className = "cart-drawer__variant";
      variant.textContent = item.variant_title;
      body.append(variant);
    }

    const properties = visibleProperties(item.properties);
    if (properties.length) {
      const list = document.createElement("ul");
      list.className = "cart-drawer__properties";
      properties.forEach(([name, value]) => {
        const property = document.createElement("li");
        property.textContent = `${name} : ${value}`;
        list.append(property);
      });
      body.append(list);
    }

    const actions = document.createElement("div");
    actions.className = "cart-drawer__item-actions";
    const quantity = document.createElement("div");
    quantity.className = "cart-drawer__quantity";
    quantity.append(
      createQuantityButton(`Diminuer la quantité de ${item.product_title}`, "decrease", "M6 12h12"),
    );
    const quantityValue = document.createElement("span");
    quantityValue.textContent = item.quantity;
    quantityValue.setAttribute("aria-label", `Quantité : ${item.quantity}`);
    quantity.append(quantityValue);
    quantity.append(
      createQuantityButton(`Augmenter la quantité de ${item.product_title}`, "increase", "M12 6v12M6 12h12"),
    );

    const remove = document.createElement("button");
    remove.className = "cart-drawer__remove";
    remove.type = "button";
    remove.dataset.cartDrawerRemove = "";
    remove.textContent = "Supprimer";
    remove.setAttribute("aria-label", `Supprimer ${item.product_title} du panier`);
    actions.append(quantity, remove);
    body.append(actions);

    const price = document.createElement("strong");
    price.className = "cart-drawer__line-price";
    price.textContent = formatMoney(item.final_line_price);
    row.append(media, body, price);
    return row;
  };

  const renderProgress = (cart) => {
    const threshold = Number(drawer.dataset.freeShippingThreshold || 0);
    const progress = threshold > 0
      ? Math.min(100, Math.round((cart.total_price / threshold) * 100))
      : 0;
    const missing = Math.max(0, threshold - cart.total_price);
    const label = drawer.querySelector("[data-cart-drawer-progress-label]");
    const track = drawer.querySelector("[data-cart-drawer-progress-track]");
    const bar = drawer.querySelector("[data-cart-drawer-progress-bar]");
    if (label) {
      label.textContent = missing > 0
        ? `Plus que ${formatMoney(missing)} pour la livraison en Point Relais offerte en France`
        : "Livraison en Point Relais offerte en France";
    }
    track?.setAttribute("aria-valuenow", String(progress));
    if (bar) bar.style.width = `${progress}%`;
  };

  const renderCart = (cart) => {
    const loading = drawer.querySelector("[data-cart-drawer-loading]");
    const empty = drawer.querySelector("[data-cart-drawer-empty]");
    const content = drawer.querySelector("[data-cart-drawer-content]");
    const footer = drawer.querySelector("[data-cart-drawer-footer]");
    const items = drawer.querySelector("[data-cart-drawer-items]");
    const count = drawer.querySelector("[data-cart-drawer-count]");
    const subtotal = drawer.querySelector("[data-cart-drawer-subtotal]");

    if (loading) loading.hidden = true;
    drawer.dataset.cartRendered = "true";
    if (count) count.textContent = cart.item_count === 1 ? "1 article" : `${cart.item_count} articles`;
    updateHeaderCount(cart.item_count);

    const isEmpty = cart.item_count === 0;
    if (empty) empty.hidden = !isEmpty;
    if (content) content.hidden = isEmpty;
    if (footer) footer.hidden = isEmpty;
    if (isEmpty || !items) return;

    items.replaceChildren(...cart.items.map(createCartItem));
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
    renderProgress(cart);
  };

  const openDrawer = () => {
    cancelScheduledClose();
    if (!drawer.open) {
      drawer.showModal();
      drawer.getBoundingClientRect();
    }
    drawer.classList.add("cart-drawer--visible");
    document.body.classList.add("cart-drawer-open");
  };

  const closeDrawer = () => {
    if (!drawer.open) return;
    cancelScheduledClose();

    const finish = () => {
      cancelScheduledClose();
      drawer.classList.remove("cart-drawer--visible");
      if (drawer.open) drawer.close();
    };

    drawer.classList.remove("cart-drawer--visible");
    if (reducedMotion.matches || !panel) {
      finish();
      return;
    }

    transitionEndHandler = (event) => {
      if (event.propertyName === "transform") finish();
    };
    panel.addEventListener("transitionend", transitionEndHandler);
    closeTimer = window.setTimeout(finish, 420);
  };

  const requestCart = async () => {
    const response = await fetch(drawer.dataset.cartUrl, {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error("Cart load failed");
    return response.json();
  };

  const refreshCart = async ({ open = false, added = false, error = "" } = {}) => {
    const loading = drawer.querySelector("[data-cart-drawer-loading]");
    if (open) openDrawer();
    if (loading) loading.hidden = drawer.dataset.cartRendered === "true";
    setBusy(true);
    setFeedback(error || (added ? "Produit ajouté au panier." : ""), error ? "error" : "success");
    try {
      renderCart(await requestCart());
    } catch {
      setFeedback("Impossible de charger le panier. Réessayez.", "error");
    } finally {
      if (loading) loading.hidden = true;
      setBusy(false);
    }
  };

  const changeLine = async (key, quantity) => {
    setBusy(true);
    setFeedback();
    try {
      const response = await fetch(drawer.dataset.cartChangeUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!response.ok) throw new Error("Cart change failed");
      renderCart(await response.json());
    } catch {
      setFeedback("Impossible de mettre à jour le panier. Réessayez.", "error");
    } finally {
      setBusy(false);
    }
  };

  const addFormToCart = async (form, submitter) => {
    const button = submitter || form.querySelector('[name="add"]');
    const wasDisabled = Boolean(button?.disabled);
    button?.setAttribute("aria-busy", "true");
    if (button) button.disabled = true;
    try {
      const response = await fetch(drawer.dataset.cartAddUrl, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: new FormData(form),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.description || "Impossible d'ajouter ce produit.");
      }
      await refreshCart({ open: true, added: true });
    } catch (error) {
      await refreshCart({ open: true, error: error.message });
    } finally {
      button?.removeAttribute("aria-busy");
      if (button) button.disabled = wasDisabled;
    }
  };

  document.addEventListener("submit", (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    const submitter = event.submitter;
    if (!submitter || submitter.name !== "add") return;
    event.preventDefault();
    addFormToCart(form, submitter);
  });

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-cart-drawer-trigger]");
    if (trigger) {
      event.preventDefault();
      refreshCart({ open: true });
      return;
    }

    if (event.target.closest("[data-cart-drawer-close]") || event.target === drawer) {
      closeDrawer();
      return;
    }

    const item = event.target.closest("[data-cart-drawer-item]");
    if (!item || drawer.getAttribute("aria-busy") === "true") return;
    const key = item.dataset.cartDrawerItem;
    const current = Number(item.querySelector(".cart-drawer__quantity span")?.textContent || 0);
    const quantityControl = event.target.closest("[data-cart-drawer-quantity]");
    if (quantityControl) {
      const next = quantityControl.dataset.cartDrawerQuantity === "increase" ? current + 1 : Math.max(0, current - 1);
      changeLine(key, next);
    } else if (event.target.closest("[data-cart-drawer-remove]")) {
      changeLine(key, 0);
    }
  });

  document.addEventListener("cart:updated", (event) => {
    refreshCart({
      open: event.detail?.open !== false,
      added: event.detail?.added === true,
    });
  });

  drawer.addEventListener("close", () => {
    cancelScheduledClose();
    drawer.classList.remove("cart-drawer--visible");
    document.body.classList.remove("cart-drawer-open");
    setFeedback();
  });

  drawer.addEventListener("cancel", (event) => {
    event.preventDefault();
    closeDrawer();
  });
})();
